import React, { useMemo, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import tz from "dayjs/plugin/timezone";
import { useQuery, useMutation } from "@apollo/client";
import ModalWrapper from "components/Modal";
import { GET_AVAILABILITY } from "apollo/queries/events";
import { UPDATE_BOOKING } from "apollo/mutations/booking";

dayjs.extend(utc);
dayjs.extend(tz);

const RescheduleModal = ({ isOpen, booking, event, onClose, onDone }) => {
  const zone = event?.timezone || "America/Chicago";
  const minDate = useMemo(
    () => (event?.startDate ? event.startDate.slice(0, 10) : dayjs().format("YYYY-MM-DD")),
    [event]
  );
  const maxDate = useMemo(
    () => (event?.endDate ? event.endDate.slice(0, 10) : minDate),
    [event, minDate]
  );

  const [date, setDate] = useState(dayjs(booking.startTime).tz(zone).format("YYYY-MM-DD"));
  const [selectedSlot, setSelectedSlot] = useState("");
  const [errorText, setErrorText] = useState("");

  const { data: avail, loading: availLoading } = useQuery(GET_AVAILABILITY, {
    variables: { eventId: booking.eventId, date },
    skip: !isOpen || !date,
    fetchPolicy: "network-only",
  });
  const slots = avail?.getAvailability || [];

  const [updateBooking, { loading: saving }] = useMutation(UPDATE_BOOKING);

  const fmtTime = (iso) => dayjs(iso).tz(zone).format("h:mm A");

  const submit = async () => {
    if (!selectedSlot) {
      setErrorText("Pick a new time first.");
      return;
    }
    try {
      await updateBooking({
        variables: { id: booking.id, input: { startTime: selectedSlot } },
      });
      onDone?.();
    } catch (err) {
      setErrorText(err?.message || "Could not reschedule.");
    }
  };

  return (
    <ModalWrapper modalIsOpen={isOpen} closeModal={onClose} isCloseIcon className="!max-w-[520px]">
      <div className="px-4 py-4 sm:px-6 sm:py-2 font-[Montserrat]">
        <h2 className="text-[#2b364b] font-bold uppercase text-[24px] leading-tight">
          Reschedule Fitting
        </h2>
        <p className="text-[#5c80b6] text-[14px] mt-1">{event?.eventName}</p>
        <p className="text-[12px] text-[#6b7280] mt-1">
          Currently: {dayjs(booking.startTime).tz(zone).format("dddd, MMM D · h:mm A")}
        </p>

        <div className="mt-5 space-y-5">
          <div>
            <div className="text-[12px] text-[#2b364b] uppercase tracking-wide">Pick a new day</div>
            <input
              type="date"
              className="w-full mt-1 px-4 h-[44px] rounded-[8px] border border-[#c3c9d4] text-[14px]"
              value={date}
              min={minDate}
              max={maxDate}
              onChange={(e) => {
                setDate(e.target.value);
                setSelectedSlot("");
              }}
            />
          </div>
          <div>
            <div className="text-[12px] text-[#2b364b] uppercase tracking-wide">Choose a time</div>
            {availLoading ? (
              <div className="text-[12px] mt-2">Loading times…</div>
            ) : slots.length === 0 ? (
              <div className="text-[13px] mt-2 text-[#6b7280]">No openings that day — try another.</div>
            ) : (
              <div className="grid grid-cols-4 gap-2 mt-2">
                {slots.map((s) => (
                  <button
                    type="button"
                    key={s.startTime}
                    onClick={() => setSelectedSlot(s.startTime)}
                    className={`h-[42px] rounded-[8px] border text-[13px] ${
                      selectedSlot === s.startTime
                        ? "bg-[#2b364b] text-white border-[#2b364b]"
                        : "bg-white text-[#2b364b] border-[#c3c9d4] hover:border-[#5c80b6]"
                    }`}
                  >
                    {fmtTime(s.startTime)}
                  </button>
                ))}
              </div>
            )}
          </div>
          {errorText && <div className="text-[13px] text-[#f25022]">{errorText}</div>}
          <div className="flex gap-3 justify-center pt-2">
            <button
              type="button"
              onClick={onClose}
              className="w-[140px] h-[48px] rounded-full border border-[#2b364b] text-[#2b364b]"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={submit}
              className="w-[160px] h-[48px] rounded-full bg-[#2b364b] text-white disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save New Time"}
            </button>
          </div>
        </div>
      </div>
    </ModalWrapper>
  );
};

export default RescheduleModal;
